import React from 'react';
import { useAutoLogout } from '../hooks/useAutoLogout';

/**
 * Composant complet pour la déconnexion automatique avec notification
 * Utilise le hook useAutoLogout pour gérer toute la logique
 */
const AutoLogoutWarning = ({ 
  timeout = 15 * 60 * 1000,      // 15 minutes
  warningTime = 2 * 60 * 1000,    // 2 minutes
  enabled = true,
  showWarning = true 
}) => {
  const {
    showWarningModal,
    timeRemaining,
    formatTime,
    resetTimer,
    logout
  } = useAutoLogout({ timeout, warningTime, enabled, showWarning });

  if (!showWarningModal || !enabled) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px',
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '40px',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
        animation: 'slideIn 0.3s ease-out'
      }}>
        <style jsx>{`
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(-20px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
        `}</style>
        
        <div style={{
          fontSize: '64px',
          marginBottom: '24px',
          animation: 'pulse 2s infinite'
        }}>
          ⏰
        </div>
        
        <h2 style={{
          color: '#1f2937',
          marginBottom: '16px',
          fontSize: '28px',
          fontWeight: '700',
          lineHeight: '1.2'
        }}>
          Session sur le point d'expirer
        </h2>
        
        <p style={{
          color: '#6b7280',
          marginBottom: '24px',
          fontSize: '16px',
          lineHeight: '1.6'
        }}>
          Vous avez été inactif depuis un moment. 
          <br />
          Pour des raisons de sécurité, votre session sera automatiquement terminée dans :
        </p>
        
        <div style={{
          fontSize: '48px',
          fontWeight: '800',
          color: '#ef4444',
          marginBottom: '32px',
          fontFamily: 'monospace',
          letterSpacing: '2px',
          padding: '16px',
          backgroundColor: '#fef2f2',
          borderRadius: '12px',
          border: '2px solid #fecaca'
        }}>
          {formatTime(timeRemaining)}
        </div>
        
        <div style={{
          display: 'flex',
          gap: '16px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={resetTimer}
            style={{
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              padding: '16px 32px',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#059669';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.4)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#10b981';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
            }}
          >
            <span style={{ fontSize: '20px' }}>🔄</span>
            Rester connecté
          </button>
          
          <button
            onClick={logout}
            style={{
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              padding: '16px 32px',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#dc2626';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 20px rgba(239, 68, 68, 0.4)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#ef4444';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
            }}
          >
            <span style={{ fontSize: '20px' }}>🚪</span>
            Se déconnecter
          </button>
        </div>
        
        <div style={{
          marginTop: '24px',
          padding: '16px',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <p style={{
            margin: 0,
            fontSize: '14px',
            color: '#6b7280',
            lineHeight: '1.4'
          }}>
            <strong>💡 Conseil :</strong> Fermez simplement cet onglet si vous avez terminé votre session.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AutoLogoutWarning;
